import { useEffect, useRef, useState } from 'react';
import { useTenantApi } from '@/hooks/useTenantApi';

const RESERVED_DISPLAY_NAMES = [
    'Brand',
    'Categories',
    'Department',
    'Super Category',
    'Subcategory',
    'UPC',
    'Product Description',
    'Parent Company',
    'Manufacturer',
] as const;

type ReservedDisplayName = (typeof RESERVED_DISPLAY_NAMES)[number];

const RESERVED_DISPLAY_NAME_BY_LOWERCASE: ReadonlyMap<string, ReservedDisplayName> = new Map(
    RESERVED_DISPLAY_NAMES.map((name) => [name.toLowerCase(), name])
);

export type DisplayNameCollision =
    | { status: 'idle' }
    | { status: 'checking' }
    | { status: 'free' }
    | { status: 'taken'; matchedDisplayName: string }
    | { status: 'failed' }
    | { status: 'reserved'; reservedName: ReservedDisplayName };

function matchReservedDisplayName(rawName: string): ReservedDisplayName | undefined {
    const trimmed = rawName.trim();
    return RESERVED_DISPLAY_NAME_BY_LOWERCASE.get(trimmed.toLowerCase());
}

export function nameCollisionBlocksSave(collision: DisplayNameCollision): boolean {
    return collision.status === 'taken' || collision.status === 'checking' || collision.status === 'reserved';
}

export function nameCollisionSaveTooltip(collision: DisplayNameCollision): string | undefined {
    switch (collision.status) {
        case 'taken':
            return `This name matches the standard characteristic "${collision.matchedDisplayName}".`;
        case 'checking':
            return 'Checking whether this name is already used.';
        case 'reserved':
            return `"${collision.reservedName}" is a reserved name and cannot be used.`;
        default:
            return undefined;
    }
}

export function useDisplayNameCollision(rawName: string): DisplayNameCollision {
    const { checkCharacteristicDisplayNameCollision } = useTenantApi();
    const checkRef = useRef(checkCharacteristicDisplayNameCollision);
    checkRef.current = checkCharacteristicDisplayNameCollision;

    const generationRef = useRef(0);
    const trimmed = rawName.trim();
    const [collision, setCollision] = useState<DisplayNameCollision>(() => {
        if (trimmed === '') {
            return { status: 'idle' };
        }
        const reservedName = matchReservedDisplayName(trimmed);
        if (reservedName !== undefined) {
            return { status: 'reserved', reservedName };
        }
        return { status: 'checking' };
    });

    useEffect(() => {
        generationRef.current += 1;
        const generation = generationRef.current;
        const controller = new AbortController();

        if (trimmed === '') {
            setCollision({ status: 'idle' });
            return () => {
                generationRef.current += 1;
                controller.abort();
            };
        }

        const reservedName = matchReservedDisplayName(trimmed);
        if (reservedName !== undefined) {
            setCollision({ status: 'reserved', reservedName });
            return () => {
                generationRef.current += 1;
                controller.abort();
            };
        }

        setCollision({ status: 'checking' });

        const timeoutId = window.setTimeout(() => {
            checkRef.current(trimmed, controller.signal).then(
                (result) => {
                    if (generation !== generationRef.current) {
                        return;
                    }
                    if (result.kind === 'free') {
                        setCollision({ status: 'free' });
                    } else {
                        setCollision({
                            status: 'taken',
                            matchedDisplayName: result.matchedDisplayName,
                        });
                    }
                },
                () => {
                    if (generation !== generationRef.current) {
                        return;
                    }
                    if (controller.signal.aborted) {
                        return;
                    }
                    setCollision({ status: 'failed' });
                }
            );
        }, 300);

        return () => {
            generationRef.current += 1;
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [trimmed]);

    return collision;
}
